import type { ChangeEvent, DragEvent, HTMLAttributes } from 'react';
import { useId, useRef, useState } from 'react';

import { Card } from '../components/card';
import { cn } from '../lib/cn';

export type UploadRejectionReason = 'empty-file' | 'invalid-type' | 'file-too-large' | 'multiple-not-allowed' | 'pre-check';
export type UploadPreCheckResult = string | null | undefined;

export interface UploadDropzoneLabels {
  dropzoneLabel: string;
  instruction: string;
  browseInstruction: string;
  invalidType: string;
  fileTooLarge: string;
  emptyFile: string;
  multipleNotAllowed: string;
  preCheckFailed: string;
  steps: readonly [string, string, string, string];
}

export interface UploadDropzoneProps extends Omit<HTMLAttributes<HTMLDivElement>, 'aria-live' | 'aria-label' | 'children' | 'role'> {
  labels: UploadDropzoneLabels;
  accept?: readonly string[];
  maxSizeMb?: number;
  activeStep?: 1 | 2 | 3 | 4;
  disabled?: boolean;
  multiple?: boolean;
  progress?: number;
  onFilesAccepted?: (files: File[]) => void;
  onRejected?: (file: File, reason: UploadRejectionReason) => void;
  onPreCheck?: (file: File) => UploadPreCheckResult | Promise<UploadPreCheckResult>;
  'aria-live'?: 'off' | 'polite' | 'assertive';
  role?: string;
}

interface Rejection {
  file: File;
  reason: UploadRejectionReason;
  message: string;
}

const DEFAULT_ACCEPT = ['.xlsx'];

function matchesAccept(file: File, accept: readonly string[]) {
  const fileType = file.type.toLowerCase();
  const extension = file.name.includes('.') ? `.${file.name.split('.').pop()?.toLowerCase() ?? ''}` : '';
  return accept.some((rawValue) => {
    const value = rawValue.trim().toLowerCase();
    if (!value) return false;
    if (value.startsWith('.')) return extension === value;
    if (value.endsWith('/*')) return fileType !== '' && fileType.startsWith(value.slice(0, -1));
    return fileType !== '' && fileType === value;
  });
}

export function UploadDropzone({ accept = DEFAULT_ACCEPT, activeStep = 1, className, disabled = false, labels, maxSizeMb = 10, multiple = false, onFilesAccepted, onPreCheck, onRejected, progress, ...props }: UploadDropzoneProps) {
  const errorId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const requestSequence = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState<Rejection[]>([]);

  const validateFile = async (file: File): Promise<Rejection | null> => {
    if (file.size === 0) return { file, reason: 'empty-file', message: labels.emptyFile };
    if (!matchesAccept(file, accept)) return { file, reason: 'invalid-type', message: labels.invalidType };
    if (file.size > maxSizeMb * 1024 * 1024) return { file, reason: 'file-too-large', message: labels.fileTooLarge };
    try {
      const preCheckMessage = await onPreCheck?.(file);
      return preCheckMessage ? { file, reason: 'pre-check', message: preCheckMessage } : null;
    } catch {
      return { file, reason: 'pre-check', message: labels.preCheckFailed };
    }
  };

  const processFiles = async (candidateFiles: File[]) => {
    if (disabled || candidateFiles.length === 0) return;
    const requestId = ++requestSequence.current;
    const allowedFiles = multiple ? candidateFiles : candidateFiles.slice(0, 1);
    const multipleRejections = multiple ? [] : candidateFiles.slice(1).map((file) => ({ file, reason: 'multiple-not-allowed' as const, message: labels.multipleNotAllowed }));
    const results = await Promise.all(allowedFiles.map(async (file) => ({ file, rejection: await validateFile(file) })));
    if (requestId !== requestSequence.current) return;
    const rejections = [...results.flatMap((result) => result.rejection ? [result.rejection] : []), ...multipleRejections];
    const accepted = results.filter((result) => result.rejection === null).map((result) => result.file);
    setErrors(rejections);
    rejections.forEach((rejection) => onRejected?.(rejection.file, rejection.reason));
    if (accepted.length) onFilesAccepted?.(accepted);
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    void processFiles(Array.from(event.target.files ?? []));
    event.target.value = '';
  };
  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    void processFiles(Array.from(event.dataTransfer.files));
  };
  const errorMessage = errors.map((error) => error.message).join(' ');

  return (
    <div className={className} {...props}>
      <ol className="mb-4 flex items-center gap-2" aria-label={labels.dropzoneLabel}>
        {labels.steps.map((label, index) => {
          const step = index + 1;
          const isComplete = step < activeStep;
          const isActive = step === activeStep;
          return <li className="flex min-w-0 flex-1 items-center gap-2" key={label}>
            <span aria-current={isActive ? 'step' : undefined} className={cn('inline-flex min-w-0 items-center gap-1 rounded-[var(--radius-badge)] border px-2 py-1 text-xs font-semibold', isComplete && 'border-transparent bg-[var(--success-light)] text-[var(--foreground)]', isActive && 'border-transparent bg-[var(--primary)] text-white', !isComplete && !isActive && 'border-[var(--border)] text-[var(--foreground-muted)]')}>
              <span aria-hidden="true">{isComplete ? '✓' : step}</span><span className="truncate">{label}</span>
            </span>
            {step < 4 ? <span aria-hidden="true" className="h-px flex-1 bg-[var(--border)]" /> : null}
          </li>;
        })}
      </ol>
      <input accept={accept.join(',')} aria-hidden="true" className="sr-only" data-testid="upload-native-input" disabled={disabled} multiple={multiple} onChange={onChange} ref={inputRef} tabIndex={-1} type="file" />
      <Card
        aria-describedby={errorMessage ? errorId : undefined}
        aria-disabled={disabled}
        aria-label={labels.dropzoneLabel}
        className={cn('flex min-h-40 cursor-pointer flex-col items-center justify-center border-2 border-dashed border-[var(--border)] p-5 text-center transition-colors hover:border-[var(--secondary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]', isDragging && 'border-[var(--secondary)] bg-[var(--secondary-light)]', disabled && 'cursor-not-allowed opacity-60')}
        onDragEnter={(event) => { event.preventDefault(); if (!disabled) { dragDepth.current += 1; setIsDragging(true); } }}
        onDragLeave={(event) => { event.preventDefault(); dragDepth.current = Math.max(0, dragDepth.current - 1); if (dragDepth.current === 0) setIsDragging(false); }}
        onDragOver={(event) => event.preventDefault()}
        onDrop={onDrop}
        onKeyDown={(event) => { if (!disabled && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); inputRef.current?.click(); } }}
        onClick={() => !disabled && inputRef.current?.click()}
        role="button"
        tabIndex={disabled ? -1 : 0}
      >
        <svg aria-hidden="true" className="mb-2 size-8 text-[var(--foreground-muted)]" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M12 16V4m0 0L8 8m4-4 4 4M5 14v5h14v-5" /></svg>
        <p className="text-sm font-medium text-[var(--foreground)]">{labels.instruction}</p>
        <p className="mt-1 text-xs text-[var(--foreground-muted)]">{labels.browseInstruction}</p>
      </Card>
      {errorMessage ? <p aria-live="assertive" className="mt-2 text-sm text-[var(--destructive)]" id={errorId} role="alert">{errorMessage}</p> : null}
      <div aria-atomic="true" aria-live="polite" className="sr-only" role="status">{progress === undefined ? '' : `${progress}%`}</div>
    </div>
  );
}
